import {
  Step,
  StepIndicator,
  StepNumber,
  Stepper as ChakraStepper,
  StepSeparator,
  StepStatus,
  Text,
} from '@chakra-ui/react';
import useCurrentUser from 'data/useCurrentUser';
import usePageVideo from 'data/usePageVideo';
import useTranslations from 'i18n';
import { useCallback, useMemo } from 'react';
import gtm from 'trackers/gtm';
import { PageCategory } from 'trackers/gtm/types';
import { PageVideoEnum } from 'types/videos';
import insertIf from 'utils/insertIf';

interface StepperProps {
  activeStep: number;
  onStepChange?: (step: number) => void;
}

const Stepper = ({ activeStep, onStepChange }: StepperProps) => {
  const video = usePageVideo(PageVideoEnum.Affiliation);
  const hasVideoStep = Boolean(video);
  const { isBOUser } = useCurrentUser();
  const { t } = useTranslations();
  const steps = useMemo(
    () => [
      {
        id: 'CompanyInfoStep',
        title: t('affiliation_flow.companyInfo', 'Company'),
        pageCategory: PageCategory.CompanyInfo,
      },
      {
        id: 'ContactInfoStep',
        title: t('affiliation_flow.contactInfo', 'Contact'),
        pageCategory: PageCategory.Contact,
      },
      {
        id: 'ProductSelectionStep',
        title: t('affiliation_flow.productSelection', 'Products'),
        pageCategory: PageCategory.Product,
      },
      {
        id: 'PaymentInfoStep',
        title: t('affiliation_flow.paymentInfo', 'Payment'),
        pageCategory: PageCategory.Payment,
      },
      ...insertIf(!isBOUser, {
        id: 'ContractConfirmationStep',
        title: t('affiliation_flow.contractConfirmation', 'Contract'),
        pageCategory: PageCategory.Contract,
      }),
      {
        id: 'LocationAddStep',
        title: t('affiliation_flow.locationAdd', 'Location'),
        pageCategory: PageCategory.Location,
      },
    ],
    [isBOUser, t],
  );
  const stepOffset = hasVideoStep ? 2 : 1;
  const activeIndex = Math.min(
    (activeStep > 0 ? activeStep : steps.length) - stepOffset,
    steps.length - 1,
  );
  const stepNumber = <StepNumber />;

  const trackStepChange = useCallback(
    (pageCategory: PageCategory, fn?: () => void) => () => {
      gtm.trackClickAffiliationStepper(pageCategory);
      fn?.();
    },
    [],
  );

  return (
    <>
      <Text variant="body.smallBold" mb={2} align="center">
        {steps[activeIndex]?.title}
      </Text>
      <ChakraStepper index={activeIndex} sx={{ height: 'auto !important' }}>
        {steps.map((step, stepIndex) => (
          <Step key={step.title || `step_${stepIndex}`}>
            <StepIndicator
              {...(onStepChange && stepIndex < activeIndex
                ? {
                    as: 'button',
                    type: 'button',
                    onClick: trackStepChange(step.pageCategory, () =>
                      onStepChange(stepIndex + stepOffset),
                    ),
                  }
                : {})}
              {...(onStepChange && stepIndex === activeIndex + 1
                ? {
                    as: 'button',
                    type: 'submit',
                    onClick: trackStepChange(step.pageCategory),
                  }
                : {})}
            >
              <StepStatus
                aria-label={step.title || `step_${stepIndex}`}
                complete={stepNumber}
                incomplete={stepNumber}
                active={stepNumber}
              />
            </StepIndicator>
            <StepSeparator />
          </Step>
        ))}
      </ChakraStepper>
    </>
  );
};

export default Stepper;
